import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLiveChallenge } from '@/hooks/useLiveChallenge';
import { liveChallengesApi } from '@/services/liveChallenges';
import LiveChallengeAuthor from './LiveChallengeAuthor';

/**
 * WaitingRoom - Lobby screen while students wait for the teacher to start.
 *
 * Polls the API every 3 seconds to detect when the teacher starts the session.
 * Also listens to WebSocket events as a faster path when available.
 */
const WaitingRoom = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { session, participantCount, currentQuestion, sessionStatus, sessionReset, clearSessionReset } = useLiveChallenge();
  const hasStarted = sessionStatus === 'in_progress';
  const pollingRef = useRef(null);
  const [dbParticipantCount, setDbParticipantCount] = useState(0);
  const [challengeUser, setChallengeUser] = useState(null);
  const totalParticipants = Math.max(participantCount, dbParticipantCount);

  // Só navega quando o admin REVELOU a primeira questão (não no Iniciar).
  // currentQuestion vem via WebSocket evento 'question_revealed'.
  useEffect(() => {
    if (currentQuestion) {
      navigate(`/desafio-ao-vivo/${id}/questao`, { replace: true });
    }
  }, [currentQuestion, navigate, id]);

  // Admin reiniciou a sessão — participantes foram destruídos no backend.
  // Volta para /entrar para que o aluno faça join novamente.
  useEffect(() => {
    if (sessionReset) {
      clearSessionReset();
      navigate('/desafio-ao-vivo/entrar', { replace: true });
    }
  }, [sessionReset, clearSessionReset, navigate]);

  // Polling apenas para atualizar contagem de participantes
  useEffect(() => {
    const poll = async () => {
      try {
        const response = await liveChallengesApi.show(id);
        const data = response?.data;
        const attrs = data?.attributes;
        const pCount = attrs?.['participants-count'] ?? 0;
        setDbParticipantCount(pCount);
        if (attrs?.user) setChallengeUser(attrs.user);
      } catch {
        // ignore
      }
    };

    poll();
    pollingRef.current = setInterval(poll, 3000);
    return () => clearInterval(pollingRef.current);
  }, [id]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-white px-4">
      <div className="flex min-h-[424px] w-[616px] max-w-full flex-col items-center justify-center gap-5 rounded-[32px] bg-primary p-8 text-center text-white">
        {/* Challenge title */}
        <h3 className="mb-0 w-[336px] max-w-full rounded-2xl border-[3px] border-solid border-primary-light bg-white px-10 py-4 text-2xl font-extrabold uppercase leading-tight text-primary">{session?.title || 'Desafio ao Vivo'}</h3>

        {challengeUser && (
          <LiveChallengeAuthor
            user={challengeUser}
            variant="dark"
            size="md"
            className="w-[336px] max-w-full rounded-2xl bg-white/10 px-4 py-3"
          />
        )}

        {/* Animated waiting indicator */}
        <p className="text-xl font-bold">{hasStarted ? 'Desafio iniciado!' : 'Prepare-se!'}</p>

        <div className="flex flex-col items-center gap-3">
          <div className="flex gap-2">
            <span className="h-3 w-3 animate-bounce rounded-full bg-white [animation-delay:0ms]" />
            <span className="h-3 w-3 animate-bounce rounded-full bg-white [animation-delay:150ms]" />
            <span className="h-3 w-3 animate-bounce rounded-full bg-white [animation-delay:300ms]" />
          </div>
          <p className="text-base font-light text-white/90">{hasStarted ? 'Prepare-se para a primeira questão...' : 'Aguardando o professor iniciar...'}</p>
        </div>

        {/* Participant count */}
        <div className="flex items-center gap-3 rounded-xl border-[3px] border-solid border-primary-light bg-white/10 px-6 py-3">
          <span className="text-3xl font-bold">{totalParticipants}</span>
          <span className="text-sm text-white/80">{participantCount === 1 ? 'participante' : 'participantes'}</span>
        </div>
      </div>
    </div>
  );
};

export default WaitingRoom;
